// src/components/ProductModal.js
import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import pb from '../pocketbaseClient';

const emptyForm = {
    nume: '',
    descriere_c1: '',
    descriere_c2: '',
    descriere_e: '',
    model_code: '',
    pret: '',
    pret_nou: '',
    f_n1: '',
    f_d1: '',
    f_n2: '',
    f_d2: '',
    f_n3: '',
    f_d3: '',
    f_n4: '',
    f_d4: '',
    f_n5: '',
    f_d5: '',
    f_n6: '',
    f_d6: '',
};

const ProductModal = ({ isOpen, onRequestClose, product, onSave }) => {
    const [formData, setFormData] = useState(emptyForm);
    const [nameResults, setNameResults] = useState([]);

    // Reset form data when the modal is opened for adding a new product
    useEffect(() => {
        if (product) {
            // Populate form data when editing a product
            setFormData({
                nume: product.nume || '',
                descriere_c1: product.descriere_c1 || '',
                descriere_c2: product.descriere_c2 || '',
                descriere_e: product.descriere_e || '',
                pret: product.pret || '',
                pret_nou: product.pret_nou || '',
                model_code: product.model_code || '',
                f_n1: product.f_n1 || '',
                f_d1: product.f_d1 || '',
                f_n2: product.f_n2 || '',
                f_d2: product.f_d2 || '',
                f_n3: product.f_n3 || '',
                f_d3: product.f_d3 || '',
                f_n4: product.f_n4 || '',
                f_d4: product.f_d4 || '',
                f_n5: product.f_n5 || '',
                f_d5: product.f_d5 || '',
                f_n6: product.f_n6 || '',
                f_d6: product.f_d6 || '',
            });
        } else {
            // Clear form data when adding a new product
            setFormData(emptyForm);
        }
    }, [product]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async () => {
        try {
            if (product && product.id) {
                // Update existing product
                await pb.collection('produse').update(product.id, formData);
            } else {
                // Create new product and relate to logged-in user
                const userId = pb.authStore?.model?.id;
                await pb.collection('produse').create({ ...formData, user: userId });
                // Ensure the form is reset after a successful create
                setFormData(emptyForm);
            }
            onSave();
            onRequestClose();
        } catch (error) {
            console.error('Error saving product:', error);
        }
    };

    const handleAutoFetch = async (modelCodeOverride) => {
        try {
            // Transform model code to correct format
            const sourceCode = (modelCodeOverride ?? formData.model_code) || '';
            const transformedModelCode = sourceCode
                .replace(/\s+/g, '')  // Remove all spaces
                .replace(/[‑-]/g, '') // Remove all hyphens (including special ones)
                .replace(/^(\d{7})(.*)$/, '$1-$2'); // Add hyphen after first 7 digits
            const response = await fetch('https://robot.bdesk.ro/webhook/2764cc29-441e-4806-82c0-058a63e01e39', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    model_code: transformedModelCode
                })
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const data = await response.json();
            setFormData(prevData => ({
                ...prevData,
                nume: data.name || '',
                descriere_c1: data.introductionText ? data.introductionText.replace(/<[^>]*>/g, '') || '' : '',
                descriere_e: data.description || '',
                pret: data.price ? data.price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') : '',
                pret_nou: data.campaignPrice ? data.campaignPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') : '',
                ...(() => {
                    let featureCount = 1;
                    const features = {};

                    if (data.valuespec1) {
                        features[`f_n${featureCount}`] = data.namespec1 || '';
                        features[`f_d${featureCount}`] = data.valuespec1;
                        featureCount++;
                    }
                    if (data.valuespec2) {
                        features[`f_n${featureCount}`] = data.namespec2 || '';
                        features[`f_d${featureCount}`] = data.valuespec2;
                        featureCount++;
                    }
                    if (data.valuespec3) {
                        features[`f_n${featureCount}`] = data.namespec3 || '';
                        features[`f_d${featureCount}`] = data.valuespec3;
                        featureCount++;
                    }
                    if (data.valuespec4) {
                        features[`f_n${featureCount}`] = data.namespec4 || '';
                        features[`f_d${featureCount}`] = data.valuespec4;
                        featureCount++;
                    }

                    return features;
                })()
            }));
        } catch (error) {
            console.error('Error sending webhook:', error);
        }
    };

    const convertKWtoCP = (text) => {
        if (!text) return text;
        return text.replace(/(\d+(?:[.,]\d+)?)\s*kW\b/gi, (match, numStr) => {
            const kw = parseFloat(numStr.replace(',', '.'));
            if (isNaN(kw)) return match;
            const cp = kw * 1.35962; // Accurate conversion factor from kW to metric horsepower
            const cpStr = cp.toFixed(1);
            return `${cpStr} CP`;
        });
    };

    const formatModelCodeForDisplay = (value) => {
        const digits = (value || '').replace(/\D/g, '').slice(0, 9);
        if (digits.length !== 9) return value || '';
        return `${digits.slice(0, 3)} ${digits.slice(3, 5)} ${digits.slice(5, 7)}-${digits.slice(7, 9)}`;
    };

    const getItemDisplayName = (item) => {
        return (
            item?.primaryArticle?.name?.longName ||
            item?.productName?.longName ||
            item?.primaryArticle?.name?.shortName ||
            item?.productName?.shortName ||
            ''
        );
    };

    const formatRonCurrency = (amount) => {
        if (amount == null) return '';
        const n = Number(amount);
        if (!Number.isFinite(n)) return '';
        return `${n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} RON`;
    };

    const renderResultItem = (item, keyPrefix) => {
        const article = item?.preferredArticle || item?.primaryArticle || null;
        const name = (
            article?.name?.longName ||
            article?.name?.shortName ||
            getItemDisplayName(item) ||
            'Fara nume'
        );
        const id = article?.id || (item?.articles && item.articles[0]?.id) || '';
        if (!article || !id) return null;
        const sources = article?.mainImage?.sources || [];
        const preferred = sources.find(s => s.config === 'WEBP_SQUARE_CONTAIN_MD') ||
            sources.find(s => s.config === 'PNG_SQUARE_CONTAIN_MD') ||
            sources.find(s => s.config === 'WEBP_SQUARE_CONTAIN_SM') ||
            sources.find(s => s.config === 'PNG_SQUARE_CONTAIN_SM') ||
            sources[0];
        const imgUrl = preferred?.url;
        const imgAlt = article?.mainImage?.alt || name;
        const formattedId = id ? formatModelCodeForDisplay(id) : '';
        // Prefer prices from the article (preferred/primary), fallback to root fields
        const articleListAmount = article?.price?.displayPrice?.amount;
        const articleCampaignAmount = article?.campaignPrice?.displayPrice?.amount;
        const rootListAmount = item?.fromListPrice?.displayPrice?.amount;
        const rootCampaignAmount = item?.fromCampaignPrice?.displayPrice?.amount;
        const listAmount = articleListAmount != null ? articleListAmount : rootListAmount;
        const campaignAmount = articleCampaignAmount != null ? articleCampaignAmount : rootCampaignAmount;
        const childArticleIds = Array.isArray(item?.articles) ?
            item.articles.map(a => a?.id).filter(childId => !!childId && childId !== id) : [];

        return (
            <div key={(id || keyPrefix) + '-' + keyPrefix}>
                <button
                    type="button"
                    onClick={() => {
                        const articleId = id;
                        if (articleId) {
                            setFormData(prev => ({
                                ...prev,
                                nume: name || prev.nume,
                                model_code: formatModelCodeForDisplay(articleId)
                            }));
                            handleAutoFetch(articleId);
                        }
                        setNameResults([]);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100"
                >
                    <div className="flex items-center gap-3">
                    {imgUrl ? (
                        <img src={imgUrl} alt={imgAlt} className="w-10 h-10 object-contain bg-white rounded" />
                    ) : (
                        <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center text-gray-400">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                                <path d="M19.5 3h-15A1.5 1.5 0 003 4.5v15A1.5 1.5 0 004.5 21h15a1.5 1.5 0 001.5-1.5v-15A1.5 1.5 0 0019.5 3zm-11.25 4.5a1.75 1.75 0 110 3.5 1.75 1.75 0 010-3.5zM4.5 18l4.875-6.094a.75.75 0 011.2.012L14 15.75l2.25-3a.75.75 0 011.2.006L19.5 18h-15z" />
                            </svg>
                        </div>
                    )}
                        <div className="flex flex-col">
                            <div className="text-sm font-medium text-gray-900">{name}</div>
                            {id ? <div className="text-xs text-gray-500">{formattedId}</div> : null}
                        </div>
                        <div className="ml-auto text-right">
                            {(() => {
                                if (campaignAmount != null && listAmount != null) {
                                    return (
                                        <>
                                            <div className="text-orange-600 text-xs">{formatRonCurrency(campaignAmount)}</div>
                                            <div className="text-gray-500 line-through text-[10px]">{formatRonCurrency(listAmount)}</div>
                                        </>
                                    );
                                }
                                if (listAmount != null) {
                                    return (
                                        <div className="text-orange-600 text-xs">{formatRonCurrency(listAmount)}</div>
                                    );
                                }
                                return null;
                            })()}
                        </div>
                    </div>
                </button>

                {childArticleIds.length > 0 && (
                    <div className="pr-3 pb-2">
                        {childArticleIds.map((childId, idx) => (
                            <button
                                key={`child-${childId}-${idx}`}
                                type="button"
                                onClick={() => {
                                    setFormData(prev => ({
                                        ...prev,
                                        nume: name || prev.nume,
                                        model_code: formatModelCodeForDisplay(childId)
                                    }));
                                    handleAutoFetch(childId);
                                    setNameResults([]);
                                }}
                                className="w-full text-left px-3 py-1 hover:bg-gray-50"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10" />
                                    <div className="flex flex-col">
                                        <div className="text-xs text-gray-700">{formatModelCodeForDisplay(childId)}</div>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const handleNameSearch = async () => {
        try {
            // Only set model_code in formatted display when searching
            setFormData(prev => ({
                ...prev,
                model_code: formatModelCodeForDisplay(prev.model_code)
            }));
            const response = await fetch('https://robot.bdesk.ro/webhook/2764cc29-441e-4806-82c0-058a63e01e39-2', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ name: formData.nume || '' })
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const data = await response.json();
            let grouped = null;
            if (data && (data.products || data.accessories || data.exactMatches)) {
                grouped = {
                    exactMatches: data?.exactMatches?.results?.[0]?.resultItem || [],
                    products: data?.products?.results?.[0]?.resultItem || [],
                    accessories: data?.accessories?.results?.[0]?.resultItem || [],
                };
                setNameResults(grouped);
            } else {
                let items = [];
                // Shape: [ { resultItem: [...] } ]
                if (Array.isArray(data) && data[0]?.resultItem) {
                    items = data[0].resultItem || [];
                } else {
                    // Backward compatibility with older shape
                    items = data?.data?.site?.products?.results?.[0]?.resultItem || [];
                }
                setNameResults(items);
            }
        } catch (error) {
            console.error('Error sending name search webhook:', error);
        }
    };

    // Removed deprecated handleSelectNameResult (legacy select) to avoid unused warnings

    return (
        <Modal ariaHideApp={false} isOpen={isOpen} onRequestClose={onRequestClose} overlayClassName={'bg-gray-900 bg-opacity-50 fixed inset-0'} className="p-4 bg-white rounded shadow-lg w-1/3 h-full right-0 absolute overflow-y-scroll">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl">{product ? 'Edit Product' : 'Add Product'}</h2>
            </div>
            <form className="flex flex-col gap-4 relative">
                <div>
                    <label htmlFor="nume" className="block text-sm font-medium text-gray-700">
                        Nume
                    </label>
                    <div className="mt-1 flex flex-nowrap gap-2 relative">
                        <input
                            id="nume"
                            name="nume"
                            placeholder="Name"
                            value={formData.nume}
                            onChange={handleChange}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleNameSearch(); } }}
                            className="border p-2 flex-1 min-w-0"
                        />
                        <button
                            type="button"
                            onClick={handleNameSearch}
                            className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-2 rounded whitespace-nowrap shrink-0 flex items-center justify-center"
                            title="Cauta dupa nume"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 103.75 3.75a7.5 7.5 0 0012.9 12.9z" />
                            </svg>
                        </button>
                        {nameResults && (Array.isArray(nameResults) ? nameResults.length > 0 : (nameResults.exactMatches?.length || nameResults.products?.length || nameResults.accessories?.length)) ? (
                            <div className="absolute top-full left-0 right-0 mt-1 z-10 border bg-white rounded shadow max-h-[70vh] overflow-auto">
                                {Array.isArray(nameResults) ? nameResults.map((item, idx) => (
                                    renderResultItem(item, idx)
                                )) : (
                                    <div className="p-2">
                                        {nameResults.exactMatches?.length ? (
                                            <div className="mb-2">
                                                {nameResults.exactMatches.map((item, idx) => renderResultItem(item, `e-${idx}`))}
                                            </div>
                                        ) : null}
                                        {nameResults.products?.length ? (
                                            <div className="mb-2">
                                                <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-gray-500">Produse</div>
                                                {nameResults.products.map((item, idx) => renderResultItem(item, `p-${idx}`))}
                                            </div>
                                        ) : null}
                                        {nameResults.accessories?.length ? (
                                            <div className="mb-2">
                                                <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-gray-500">Accesorii</div>
                                                {nameResults.accessories.map((item, idx) => renderResultItem(item, `a-${idx}`))}
                                            </div>
                                        ) : null}
                                    </div>
                                )}
                            </div>
                        ) : null}
                    </div>
                </div>

                <div>
                    <label htmlFor="descriere_c1" className="block text-sm font-medium text-gray-700">
                        Descriere stanga
                    </label>
                    <div className="mt-1 flex flex-nowrap gap-2 items-start">
                        <textarea
                            id="descriere_c1"
                            name="descriere_c1"
                            placeholder="Description C1"
                            value={formData.descriere_c1}
                            onChange={handleChange}
                            className="border p-2 w-full h-32 flex-1 min-w-0"
                        />
                        {(formData.descriere_c1 || '').match(/kW/i) && (
                            <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, descriere_c1: convertKWtoCP(prev.descriere_c1) }))}
                                className="bg-indigo-500 text-white px-3 py-2 rounded hover:bg-indigo-600 whitespace-nowrap shrink-0"
                            >
                                Transformare CP
                            </button>
                        )}
                    </div>
                </div>

                <div>
                    <label htmlFor="descriere_c2" className="block text-sm font-medium text-gray-700">
                        Descriere dreapta
                    </label>
                    <div className="mt-1 flex flex-nowrap gap-2 items-start">
                        <textarea
                            id="descriere_c2"
                            name="descriere_c2"
                            placeholder="Description C2"
                            value={formData.descriere_c2}
                            onChange={handleChange}
                            className="border p-2 w-full h-32 flex-1 min-w-0"
                        />
                        {(formData.descriere_c2 || '').match(/kW/i) && (
                            <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, descriere_c2: convertKWtoCP(prev.descriere_c2) }))}
                                className="bg-indigo-500 text-white px-3 py-2 rounded hover:bg-indigo-600 whitespace-nowrap shrink-0"
                            >
                                Transformare CP
                            </button>
                        )}
                    </div>
                </div>

                <div>
                    <label htmlFor="descriere_e" className="block text-sm font-medium text-gray-700">
                        Descriere extra
                    </label>
                    <div className="mt-1 flex flex-nowrap gap-2">
                        <input
                            id="descriere_e"
                            name="descriere_e"
                            placeholder="Description E"
                            value={formData.descriere_e}
                            onChange={handleChange}
                            className="border p-2 flex-1 min-w-0"
                        />
                        {(formData.descriere_e || '').match(/kW/i) && (
                            <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, descriere_e: convertKWtoCP(prev.descriere_e) }))}
                                className="bg-indigo-500 text-white px-3 py-2 rounded hover:bg-indigo-600 whitespace-nowrap shrink-0"
                            >
                                Transformare CP
                            </button>
                        )}
                    </div>
                </div>

                <div>
                    <label htmlFor="pret" className="block text-sm font-medium text-gray-700">
                        Pret
                    </label>
                    <input
                        id="pret"
                        name="pret"
                        type="text"
                        placeholder="Price"
                        value={formData.pret}
                        onChange={handleChange}
                        className="mt-1 border p-2 w-full"
                    />
                </div>

                <div>
                    <label htmlFor="pret_nou" className="block text-sm font-medium text-gray-700">
                        Pret redus
                    </label>
                    <input
                        id="pret_nou"
                        name="pret_nou"
                        type="text"
                        placeholder="New Price"
                        value={formData.pret_nou}
                        onChange={handleChange}
                        className="mt-1 border p-2 w-full"
                    />
                </div>

                <div>
                    <label htmlFor="model_code" className="block text-sm font-medium text-gray-700">
                        Cod produs
                    </label>
                    <div className="mt-1 flex flex-nowrap gap-2">
                        <input
                            id="model_code"
                            name="model_code"
                            placeholder="Model Code"
                            value={formData.model_code}
                            onChange={handleChange}
                            className="border p-2 flex-1 min-w-0"
                        />
                        <button
                            type="button"
                            onClick={() => handleAutoFetch()}
                            className="bg-indigo-500 text-white px-4 py-2 rounded hover:bg-indigo-600 whitespace-nowrap shrink-0"
                        >
                            Preluare automata
                        </button>
                    </div>
                </div>

                {[...Array(6)].map((_, index) => (
                    <div key={index} className="gap-2 flex flex-col">
                        <div>
                            <label htmlFor={`f_n${index + 1}`} className="block text-sm font-medium text-gray-700">
                                Nume functie {index + 1}
                            </label>
                            <input
                                id={`f_n${index + 1}`}
                                name={`f_n${index + 1}`}
                                placeholder={`Feature ${index + 1} Name`}
                                value={formData[`f_n${index + 1}`]}
                                onChange={handleChange}
                                className="mt-1 border p-2 w-full"
                            />
                        </div>

                        <div>
                            <label htmlFor={`f_d${index + 1}`} className="block text-sm font-medium text-gray-700">
                                Descriere functie {index + 1}
                            </label>
                            <div className="mt-1 flex flex-nowrap gap-2">
                                <input
                                    id={`f_d${index + 1}`}
                                    name={`f_d${index + 1}`}
                                    placeholder={`Feature ${index + 1} Description`}
                                    value={formData[`f_d${index + 1}`]}
                                    onChange={handleChange}
                                    className="border p-2 flex-1 min-w-0"
                                />
                                {((formData[`f_d${index + 1}`] || '').match(/kW/i)) && (
                                    <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({
                                            ...prev,
                                            [`f_d${index + 1}`]: convertKWtoCP(prev[`f_d${index + 1}`])
                                        }))}
                                        className="bg-indigo-500 text-white px-3 py-2 rounded hover:bg-indigo-600 whitespace-nowrap shrink-0"
                                    >
                                        Transformare CP
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
                <div className='sticky -bottom-4 w-full bg-white'>

                    <button type="button" onClick={handleSubmit} className="bg-blue-500 text-white p-2 my-2 rounded w-full">
                        Salveaza
                    </button>
                </div>
            </form>

        </Modal>
    );
};

export default ProductModal;
